/**
 * 임대 계산식 프리셋 API — /api/admin/rental-preset/* (마이그 177 / 180).
 *
 * 시트 `임대 견적 계산기 v.0813` 의 기본 정책·추가 정책 두 표가 이 API 의
 * 대상이다. 상품군 열이 프리셋이고 정책 행이 원가 항목이며, **열도 행도 전부
 * 데이터**라 항목을 늘리는 데 배포가 필요 없다.
 *
 * ★ 계산 API 는 없다. 단가 계산은 `@hcltech/shared` 의 `computeRentalPricing`
 *   하나뿐이고 브라우저에서 그대로 돈다. 미리보기는 이 API 로 프리셋을 받아
 *   화면에서 계산한다 — 서버에 계산 엔드포인트를 하나 더 두면 같은 계산이 두
 *   경로로 갈라진다. 서버가 계산을 다시 하는 지점은 견적 저장뿐이다.
 *
 * PUT/DELETE 는 http.ts 에 헬퍼가 없어 quote-api 와 같은 방식으로 loadingFetch 를
 * 직접 쓴다(getJson/postJson 만 있다).
 */

import {
  API_BASE, authHeaders, getJson, postJson, loadingFetch,
  parseResponse, errorDetail,
} from './http';
import {
  RentalPresetSchema, RentalPresetListResponseSchema,
  type RentalPreset, type RentalPresetItem, type RentalPresetDefaultFor,
  type RentalCostKind,
} from './schemas';

export type { RentalPreset, RentalPresetItem, RentalPresetDefaultFor, RentalCostKind };

export interface RentalPresetListResponse {
  presets: RentalPreset[];
}

// ─── 조회 ─────────────────────────────────────────────────────────────
/**
 * GET / — 프리셋 + 항목 목록.
 *
 * `includeInactive` 는 설정 화면의 '비활성 포함' 토글 전용이다. 견적 계산
 * 모달은 켜지 않는다 — 꺼 둔 프리셋이 선택지에 나오면 안 된다.
 */
export async function listRentalPresets(
  opts: { includeInactive?: boolean; signal?: AbortSignal } = {},
): Promise<RentalPresetListResponse> {
  const qs = opts.includeInactive ? '?include_inactive=true' : '';
  return getJson<RentalPresetListResponse>(
    `/api/admin/rental-preset${qs}`,
    { signal: opts.signal, schema: RentalPresetListResponseSchema },
  );
}

/** GET /:id — 단건. **꺼 둔 항목까지** 온다(설정 화면이 되살릴 수 있어야 한다). */
export async function fetchRentalPreset(
  id: string,
  opts: { signal?: AbortSignal } = {},
): Promise<RentalPreset> {
  return getJson<RentalPreset>(
    `/api/admin/rental-preset/${encodeURIComponent(id)}`,
    { ...opts, schema: RentalPresetSchema },
  );
}

// ─── 쓰기 ─────────────────────────────────────────────────────────────
export interface RentalPresetCreateInput {
  name:                 string;
  /** null 이면 자동 선택 대상이 아니다 — 사람이 골라야 나온다(워크스테이션 등). */
  default_for:          RentalPresetDefaultFor | null;
  target_margin_rate:   number;
  /** 할증 꼭짓점 T(개월). 이 개월의 할증률이 아래 S 다. */
  short_term_months:    number;
  /** 꼭짓점 T 에서의 할증률 S. */
  short_term_surcharge: number;
  /** 정상 임대 개월 L. 이 개월부터 할증 0. 생략하면 서버가 12 를 쓴다. */
  normal_term_months?:  number;
  /** 꼭짓점 미만 1개월당 가산율 U. 생략하면 서버가 0.1 을 쓴다. */
  short_term_step_up?:  number;
  penalty_rate:         number;
  penalty_adjust:       number;
  sort_order?:          number;
  /**
   * 복제 원본. 지정하면 그 프리셋의 **활성 항목만** 그대로 옮긴다.
   * 정책값은 여기 보낸 값을 쓴다 — '복제 후 이름·마진율 수정'을 한 번에 보내도
   * 방금 고친 값이 원본 값으로 되돌아가지 않는다.
   */
  copy_from_preset_id?: string | null;
}

/** POST / — 생성. 이름/기본지정이 겹치면 409 (메시지가 어느 쪽인지 알려준다). */
export async function createRentalPreset(
  body: RentalPresetCreateInput,
): Promise<RentalPreset> {
  return postJson<RentalPreset>('/api/admin/rental-preset', body, RentalPresetSchema);
}

/**
 * PUT /:id — 보낸 필드만 바뀐다.
 *
 * ★ `default_for` 만 규칙이 다르다. **키를 빼면 유지, null 을 보내면 해제**다.
 *   `undefined` 로 보내면 JSON 직렬화에서 키가 사라지므로 '유지'가 된다.
 */
export interface RentalPresetUpdateInput {
  name?:                 string;
  default_for?:          RentalPresetDefaultFor | null;
  target_margin_rate?:   number;
  short_term_months?:    number;
  short_term_surcharge?: number;
  normal_term_months?:   number;
  short_term_step_up?:   number;
  penalty_rate?:         number;
  penalty_adjust?:       number;
  sort_order?:           number;
  is_active?:            boolean;
}

export async function updateRentalPreset(
  id: string,
  body: RentalPresetUpdateInput,
): Promise<RentalPreset> {
  return putPreset(`/${encodeURIComponent(id)}`, body, 'PUT /api/admin/rental-preset/:id');
}

/**
 * DELETE /:id — 비활성화다(하드 삭제가 아니다).
 *
 * 지우지 않는 이유는 과거 견적이 어느 프리셋에서 나왔는지 되짚을 근거를
 * 남기기 위해서다. 끄면 이름과 기본지정 자리가 곧바로 풀려 같은 이름으로
 * 새 프리셋을 만들 수 있다.
 */
export async function deactivateRentalPreset(id: string): Promise<RentalPreset> {
  const path = `/api/admin/rental-preset/${encodeURIComponent(id)}`;
  const res  = await loadingFetch(`${API_BASE}${path}`, {
    method: 'DELETE', headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await failMessage(res, '프리셋 비활성화 실패'));
  return parseResponse(await res.json(), RentalPresetSchema, `DELETE ${path}`);
}

// ─── 항목 집합 저장 ───────────────────────────────────────────────────
export interface RentalPresetItemInput {
  /** 기존 항목이면 id. 새 항목이면 생략한다. */
  id?:              string | null;
  label:            string;
  kind:             RentalCostKind;
  /** kind 가 단위를 정한다 — 비율이면 0.25(=25%), 금액이면 120000. */
  value:            number;
}

/**
 * PUT /:id/items — 보낸 배열이 곧 결과다(선언적 저장).
 *
 * - **배열 순서가 표시 순서**다. sort_order 를 따로 계산해 보내지 않는다.
 * - 빠진 항목은 삭제가 아니라 비활성화된다. 빈 배열은 '전부 비움'이라는
 *   유효한 저장이다.
 * - 껐던 항목을 같은 이름으로 다시 넣으면 서버가 그 행을 되살린다(id 를
 *   몰라도 된다). 대신 한 요청 안에 같은 이름이 둘이면 400 이다.
 */
export async function saveRentalPresetItems(
  id: string,
  items: RentalPresetItemInput[],
): Promise<RentalPreset> {
  return putPreset(
    `/${encodeURIComponent(id)}/items`, { items },
    'PUT /api/admin/rental-preset/:id/items',
  );
}

// ─── 내부 ─────────────────────────────────────────────────────────────
async function putPreset(
  suffix: string, body: unknown, ctx: string,
): Promise<RentalPreset> {
  const res = await loadingFetch(`${API_BASE}/api/admin/rental-preset${suffix}`, {
    method: 'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await failMessage(res, '프리셋 저장 실패'));
  return parseResponse(await res.json(), RentalPresetSchema, ctx);
}

/**
 * 서버 메시지를 그대로 살린다.
 * 409 는 "같은 이름의 프리셋이 이미 있습니다" / "이미 같은 조건의 기본
 * 프리셋이 있습니다" 로 원인이 갈리므로 뭉뚱그리면 어느 칸을 고쳐야 할지
 * 알 수 없다.
 */
async function failMessage(res: Response, fallback: string): Promise<string> {
  const detail = errorDetail(await res.json().catch(() => null));
  return detail || `${fallback} (HTTP ${res.status})`;
}
